import MaskedInput from "react-input-mask"
import { useController } from "react-hook-form"
import mergeDeep from "@mobi/utils/object/mergeDeep"
import isCepValid from "@mobi/utils/validators/cep"
import ICepProps from "./types"
import service from "./service"

import { Input } from "@mobi/ds"
import { useState, useRef } from "react"
import { handleChange, handleBlur } from "../../handlerMethods"

import CepLoader from "./components/CepLoader"

const DEFAULT_MESSAGES = {
  required: "Campo obrigatório",
  cep: "Cep inválido",
}

const Cep = ({
  control,
  name = "cep",
  label = "cep",
  type = "text",
  inputmode = "numeric",
  pattern = "[0-9-]*",
  required = true,
  delayInvalidError = false,
  validators: { rules: propRules = {}, messages: propMessages = {} } = {},
  onChange = () => {},
  onBlur = () => {},
  onFetch,
  onFinish,
  onFetchLoader = true,
  onResult,
  disabled,
  ...props
}: ICepProps) => {
  const currentValue = useRef("")
  const finishValidation = () => {
    if (onFetchLoader) setIsLoading(false)
    if (onFinish) onFinish()
  }

  const hasFetched = (value: string) => {
    if (currentValue.current === value) return true
    currentValue.current = value
    return false
  }

  const DEFAULT_RULES = {
    validate: {
      cep: async (value: string) => {
        try {
          if (isCepValid(value)) {
            if (!hasFetched(value)) {
              if (onFetchLoader) setIsLoading(true)
              if (onFetch) onFetch()
              const result = await service.getCep(value)
              if (!result?.erro) {
                setIsViaCepError(false)
                if (onResult) onResult(result)
              } else {
                setIsViaCepError(true)
              }

              finishValidation()

              return !result?.erro
            }
            return !isViaCepError
          }
        } catch {
          setIsViaCepError(true)
        }
        finishValidation()
        return false
      },
    },
  }
  const rules = mergeDeep(DEFAULT_RULES, propRules, { required })
  const errorMessages = mergeDeep(DEFAULT_MESSAGES, propMessages)

  const {
    field,
    fieldState: { invalid, error, isTouched, isDirty },
  } = useController({ name, control, rules })
  const [isViaCepError, setIsViaCepError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const timer = useRef<NodeJS.Timeout | null>(null)
  const [typing, setTyping] = useState<boolean>(false)
  const handleTyping = () => {
    setTyping(true)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setTyping(false), 1000)
  }

  const hasFormError = isTouched && invalid && isDirty
  const isInvalid = !typing && (hasFormError || isViaCepError)

  return (
    <MaskedInput
      mask={field.value ? "99999-999" : null}
      value={field.value}
      ref={field.ref}
      disabled={disabled}
      onChange={(event) => {
        if (delayInvalidError) handleTyping()
        handleChange(event, field, onChange)
      }}
      onBlur={(event) => handleBlur(event, field, onBlur)}
      maskPlaceholder={null}
    >
      <Input.Default
        {...props}
        label={label}
        type={type}
        inputMode={inputmode}
        pattern={pattern}
        isValid={!isInvalid}
        errorMessage={errorMessages[error?.type] || errorMessages?.cep}
        aria-required={required}
        disabled={disabled}
        rightIcon={isLoading ? <CepLoader /> : ""}
        name={field.name}
      />
    </MaskedInput>
  )
}

export default Cep
